/* eslint-disable jsx-a11y/role-supports-aria-props */
import { ModalButton } from '@/components/common/modal-button';
import CloseIcon from '@/components/icons/CloseIcon';
import { ZOOM_BAR_PRESETS, zoomLevelToIndex, zoomLevelToScale, zoomPresetScaleToLevel } from '@/constants/zoom';
import useBaseSize from '@/hooks/useBaseSize';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useConfigStore } from '@/stores';
import { getSize } from '@/utils/size';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useAccessibilityTTS } from '@/hooks';

const ZOOM_PRESET_LABEL_CLASS = 'text-[calc(30*var(--vh-unit))] leading-[calc(32*var(--vh-unit))] font-[700] tracking-[calc(-0.66*var(--vw-unit))]';

interface ZoomButtonsProps {
    isOpen: boolean;
    onClose?: () => void;
    onZoom?: (zoom: number) => void;
    className?: string;
    isZoomedIn?: boolean;
}

export function ZoomButtons({ isOpen, onClose, onZoom, className }: ZoomButtonsProps) {
    const { baseWidth } = useBaseSize();
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { releaseFocus } = useFocusTrap();
    const zoomLevel = useConfigStore((state) => state.config?.ui?.zoomLevel);
    const zoomIndex = zoomLevelToIndex(zoomLevel);

    // Track if button was clicked to prevent double TTS on focus + click
    const [isZoomClicking, setIsZoomClicking] = useState(false);

    const { speak } = useAccessibilityTTS();

    const textToSpeechZoomLevel = (zoomLevelNumber: number) => {
        const mapMessage: Record<number, string> = {
            2: t('zoom.buttons.tts.zoomX1.5'),
            3: t('zoom.buttons.tts.zoomX2'),
            4: t('zoom.buttons.tts.zoomX3'),
        } as const;

        return mapMessage[zoomLevelNumber] ?? '';
    };

    // Get the next zoom level index (cycles between 2, 3, 4)
    const getNextZoomLevel = () => {
        return zoomIndex >= 4 ? 2 : zoomIndex + 1;
    };

    // Zoom messages
    const presetZoomMessage = (presetScale: number) => {
        const level = zoomPresetScaleToLevel(presetScale);
        return `${t('footer.barrier_free.zoom_in')}, ${t('common.button.tts.toggle_button')}, ${textToSpeechZoomLevel(level)}`;
    };

    const nextZoomMessage = `${t('footer.barrier_free.zoom_in')}, ${t('common.button.tts.toggle_button')}, ${textToSpeechZoomLevel(getNextZoomLevel())}`;

    const handleZoomMouseDown = () => {
        setIsZoomClicking(true);
    };

    const handleZoom = (presetScale: number) => {
        speak(nextZoomMessage);
        setTimeout(() => {
            setIsZoomClicking(false);
        }, 150);
        onZoom?.(zoomPresetScaleToLevel(presetScale));
    };

    // Apply focus trap when component is open
    useEffect(() => {
        if (isOpen && containerRef.current) {
            // const cleanup = trapFocus(containerRef.current, true);

            // Focus on the first focusable element
            const focusableElements = containerRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as NodeListOf<HTMLElement>;

            if (focusableElements.length > 0) {
                // Use setTimeout to ensure the element is rendered and focusable
                setTimeout(() => {
                    focusableElements[0].focus();
                }, 100);
            }

            return () => {
                // cleanup();
            };
        } else {
            releaseFocus();
        }
    }, [isOpen, releaseFocus]);

    if (!isOpen) return null;

    return (
        <div
            ref={containerRef}
            data-zoom-buttons="true"
            tabIndex={0}
            // role="dialog"
            className={clsx(
                styles['zoom-buttons'],
                'widescreen:top-[calc(906*var(--vh-unit))] widescreen:bottom-[calc(60*var(--vh-unit))] widescreen:left-[calc(737*var(--vw-unit))] widescreen:right-[calc(737*var(--vw-unit))] fixed top-[calc(1743*var(--vh-unit))] right-[calc(317*var(--vw-unit))] bottom-[calc(63*var(--vh-unit))] left-[calc(317*var(--vw-unit))] z-[100] flex h-[calc(114*var(--vh-unit))] w-fit flex-row gap-[calc(12*var(--vw-unit))] rounded-[calc(12*var(--vh-unit))] p-[calc(12*var(--vh-unit))]',
                className
            )}
            data-nav="zoom"
        >
            <div className="bg-dark-transparent-60-to-80 flex flex-row justify-center justify-items-center gap-[calc(12*var(--vh-unit))] rounded-[calc(12*var(--vh-unit))] p-[calc(10*var(--vh-unit))]">
                {ZOOM_BAR_PRESETS.map((preset) => {
                    return (
                        <ModalButton
                            key={preset.id}
                            data-tts-scope="zoom-button"
                            onClick={() => handleZoom(preset.zoomMultiplier)}
                            onMouseDown={handleZoomMouseDown}
                            data-interactive
                            type="button"
                            size="zoom"
                            variant={preset.zoomMultiplier === zoomLevelToScale(zoomLevel) ? 'primary' : 'tertiary'}
                            style={{
                                paddingTop: getSize(29, baseWidth),
                                paddingBottom: getSize(29, baseWidth),
                                paddingLeft: getSize(27, baseWidth),
                                paddingRight: getSize(27, baseWidth),
                            }}
                            tabIndex={0}
                            onFocus={() => {
                                if (!isZoomClicking) {
                                    speak(presetZoomMessage(preset.zoomMultiplier));
                                }
                            }}
                        >
                            <span className={ZOOM_PRESET_LABEL_CLASS}>{t(preset.labelKey)}</span>
                        </ModalButton>
                    );
                })}
            </div>

            <ModalButton
                onClick={onClose}
                role="button"
                data-interactive
                type="button"
                size="fit"
                variant="close"
                aria-label={t('zoom.buttons.tts.close')}
                style={{
                    paddingTop: `${getSize(29, baseWidth)}`,
                    paddingBottom: `${getSize(29, baseWidth)}`,
                    paddingLeft: `${getSize(28, baseWidth)}`,
                    paddingRight: `${getSize(28, baseWidth)}`,
                }}
                tabIndex={0}
            >
                <div className="flex flex-row gap-[calc(12*var(--vh-unit))]">
                    <div className="flex items-center justify-center">
                        <CloseIcon width={24} height={24} />
                    </div>
                    <span className="text-[calc(30*var(--vh-unit))] leading-[calc(32*var(--vh-unit))] font-[500] tracking-[calc(-0.66*var(--vw-unit))]">
                        {t('zoom.buttons.text.close')}
                    </span>
                </div>
            </ModalButton>
        </div>
    );
}
